import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const TooltipRoot = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // Tooltip is portal-mounted to <body> so it escapes any ancestor
        // overflow:hidden / transform clipping contexts. z-[100] keeps it above
        // dropdowns/sticky headers. Solid bg-zinc-900 + visible border so the
        // text never bleeds into rows beneath it.
        "z-[100] overflow-hidden rounded-md bg-zinc-900 px-3 py-1.5 text-xs text-white shadow-lg border border-zinc-700 max-w-[360px] whitespace-normal break-words animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

// Simple Tooltip wrapper for easy usage (backward compatible)
export interface TooltipProps extends React.HTMLAttributes<HTMLDivElement> {
  content: string;
  children: React.ReactNode;
}

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  ({ content, children, ...props }, ref) => {
    return (
      <TooltipProvider delayDuration={200}>
        <TooltipRoot>
          <TooltipTrigger asChild>
            <div ref={ref} className="inline-flex" {...props}>
              {children}
            </div>
          </TooltipTrigger>
          <TooltipContent>{content}</TooltipContent>
        </TooltipRoot>
      </TooltipProvider>
    )
  }
)
Tooltip.displayName = "Tooltip"

export { Tooltip, TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent }
